import * as React from 'react';
import { FC, useEffect, useState } from 'react';
import { PrimaryButton } from '@gje/buttons/lib/primary-button';
import { getText } from '../../../services/text/text-service';
import { Title } from '@gje/typography/lib/title';
import { KeyboardArrowDown } from '@gje/icons/lib/keyboard-arrow-down';
import HelpBox from '../HelpBox/HelpBox';
import posed from 'react-pose';
import CoverageListElement from '../ExtraCoverage/CoverageListElement';
import { resolvePaymentTerm } from '../../../services/calculation-data/calculation-data-service';
import { getPriceWithoutDiscount } from '../../../services/customer/customer-service';
import { Coverage } from '../../../types/Coverage';
import withWidth, { WithWidthProps } from '../../../hoc/withWidth';
import { numberWithSpaces } from '../../../utils/stringUtils';
import classNames from 'classnames';
import MobileExpandableCard from '../ExpandableCard/MobileExpandableCard';
import { isIPad13, isMobile, isTablet } from 'react-device-detect';
import {
    isOkq8,
    isVillaagarna
} from '../../../services/partners/partnersService';

const selectedCardImage = require('../../../images/selectedCard.png');
const selectedCardImageOK = require('../../../images/selectedCardOK.png');

interface CardsProps extends WithWidthProps {
    insuranceData: any;
    allCoveragesList: Coverage[];
    isOpen: boolean;
    handleClick: Function;
    price: string | number;
    paymentTerm: number;
    recommended: boolean;
    recommendationText: string;
    additionalContentMap?: any[];
    closedCardHeight?: number;
    mobileFontSize?: number;
    removeButtonMargin?: any;
    alignIfNoRecommendationText?: boolean;
    priceMinHeight?: boolean;
    smallPadding?: boolean;
    noPadding?: boolean;
    customButtonMargin?: any;
    customListPadding?: any;
    isAgeLimitExceededForFullInsurance?: boolean;
    campaignDiscountPercentage?: number;
}

const Card = posed.div({
    closed: {
        height: 'auto',
        backgroundColor: isOkq8() ? '#FFFFFF' : '#f0ece6',
        transition: {
            delay: 100,
            ease: 'easeIn',
            duration: 300
        }
    },
    open: {
        height: 'auto',
        backgroundColor: isVillaagarna()
            ? '#EEF9EA'
            : isOkq8()
            ? '#FFFFFF'
            : '#dfe9e1',
        transition: {
            delay: 100,
            ease: 'easeIn',
            duration: 300
        }
    },
    props: { closedCardHeight: 434 }
});

const ExpandablePanel = posed.div({
    hidden: {
        height: 'auto',
        transition: {
            delay: 1,
            ease: 'easeIn',
            duration: 300
        }
    },
    visible: {
        height: 'auto',
        transition: {
            ease: 'easeIn',
            duration: 300
        }
    }
});

const ExtraCoveragesList = posed.ul({
    visible: {
        delay: 1000,
        staggerChildren: 50,
        staggerDirection: 1
    },
    hidden: {
        staggerChildren: 50,
        staggerDirection: 1
    }
});

const ButtonContainer = posed.div({
    closed: {
        delay: 500,
        opacity: 1
    },
    open: {
        opacity: 0,
        zIndex: -1
    }
});

const ExpandedCard: FC<CardsProps> = props => {
    console.log(props);
    const [openCoverageIndex, setOpenCoverageIndex] = useState(-1);

    const priceToHigh = typeof props.price === 'string';

    useEffect(() => {
        setOpenCoverageIndex(-1);
    }, [props.isOpen]);

    const renderExtraCoveragesList = (
        availableCoverages: string[],
        allCoverages: Coverage[],
        listVisible: boolean
    ) => {
        return allCoverages.map((coverage, index) => (
            <CoverageListElement
                key={index}
                coverageData={coverage}
                availableCoverages={availableCoverages}
                visible={listVisible}
                isOpen={openCoverageIndex === index}
                alwaysExpanded={true}
                onClick={() =>
                    setOpenCoverageIndex(
                        index === openCoverageIndex ? -1 : index
                    )
                }
            />
        ));
    };

    const renderMobileView = () => {
        return <MobileExpandableCard {...props} />;
    };

    const handleCardClick = e => {
        if (
            !e.target.classList.contains('dont-hide-card-click') &&
            e.target.tagName != 'H6'
        ) {
            setOpenCoverageIndex(-1);
        }
    };

    const renderView = () => (
        <div className={'no-padding col-sm-6 collapsed-card'}>
            <Card
                pose={props.isOpen ? 'open' : 'closed'}
                onClick={handleCardClick}
                closedCardHeight={
                    props.closedCardHeight ? props.closedCardHeight : 234
                }>
                {props.isOpen && (
                    <div
                        className={classNames('selected-card-image-container', {
                            'margin-padding-0': isOkq8()
                        })}>
                        <img
                            className="selected-card-image"
                            src={
                                isOkq8()
                                    ? selectedCardImageOK
                                    : selectedCardImage
                            }
                            alt="selected-insurance-img"
                        />
                    </div>
                )}
                <div className="expanded-card-content">
                    <Title
                        className={classNames({ 'okq8-black': isOkq8() })}
                        tag={'h4'}>
                        {props.insuranceData.name}
                    </Title>
                    {props.recommended === true ? (
                        <HelpBox
                            header={getText(
                                'webshopsweden.common.recommendation'
                            )}
                            helpText={props.recommendationText}
                            id={props.insuranceData.name + '-helpbox'}
                            useBlue={isOkq8()}
                        />
                    ) : (
                        props.alignIfNoRecommendationText && (
                            <HelpBox
                                isHidden={true}
                                header={getText('')}
                                helpText={''}
                                id={props.insuranceData.name + '-helpbox'}
                            />
                        )
                    )}
                    <div className={props.priceMinHeight && 'min-height-42'}>
                        {props.campaignDiscountPercentage > 0 && !priceToHigh && (
                            <Title
                                tag={'h4'}
                                className={classNames(
                                    {
                                        'line-height-1-1':
                                            priceToHigh && !props.priceMinHeight
                                    },
                                    {
                                        'okq8-font-bold okq8-blue-color': isOkq8()
                                    },
                                    {
                                        'margin-top-24':
                                            !props.recommended && isOkq8()
                                    },
                                    'cross-mark'
                                )}>
                                {`${numberWithSpaces(
                                    getPriceWithoutDiscount(
                                        Number(props.price),
                                        Number(props.campaignDiscountPercentage)
                                    )
                                )} ${resolvePaymentTerm(
                                    props.paymentTerm,
                                    props.price
                                )}`}
                            </Title>
                        )}
                        <Title
                            tag={priceToHigh ? 'h6' : 'h3'}
                            className={classNames(
                                {
                                    'line-height-1-1':
                                        priceToHigh && !props.priceMinHeight
                                },
                                { 'okq8-font-bold okq8-blue-color': isOkq8() },
                                {
                                    'margin-top-24':
                                        !props.recommended &&
                                        isOkq8() &&
                                        !props.campaignDiscountPercentage
                                }
                            )}>
                            {priceToHigh
                                ? props.price
                                : `${numberWithSpaces(
                                      Number(props.price)
                                  )} ${resolvePaymentTerm(
                                      props.paymentTerm,
                                      props.price
                                  )}`}
                        </Title>
                    </div>
                    {props.additionalContentMap ? (
                        props.recommended === true ? (
                            <div>
                                {props.additionalContentMap.map(entry => (
                                    <div key={entry.title}>
                                        {`${Object.values(entry)[0]}: ${
                                            Object.values(entry)[1]
                                        }`}
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="expandable-card-additional-content">
                                {props.additionalContentMap.map(entry => (
                                    <div key={entry.title}>
                                        {`${Object.values(entry)[0]}: ${
                                            Object.values(entry)[1]
                                        }`}
                                    </div>
                                ))}
                            </div>
                        )
                    ) : (
                        ''
                    )}
                </div>
                <ExpandablePanel
                    key="expandablePanel"
                    className={
                        props.noPadding
                            ? 'no-padding-bottom'
                            : props.smallPadding
                            ? 'expandable-panel-small-padding'
                            : 'expandable-panel'
                    }
                    pose={props.isOpen ? 'visible' : 'hidden'}>
                    <ExtraCoveragesList
                        className={classNames(
                            'extra-coverages-list',
                            props.customListPadding
                                ? `padding-bottom-${props.customListPadding}`
                                : ''
                        )}
                        key="extraCoveragesList"
                        pose={props.isOpen ? 'visible' : 'hidden'}>
                        {renderExtraCoveragesList(
                            props.insuranceData.availableCoverages,
                            props.allCoveragesList,
                            props.isOpen
                        )}
                    </ExtraCoveragesList>
                </ExpandablePanel>
            </Card>
            {!isOkq8() && (
                <ButtonContainer
                    pose={props.isOpen ? 'open' : 'closed'}
                    className={classNames(
                        props.removeButtonMargin
                            ? ''
                            : `margin-top-${
                                  props.customButtonMargin
                                      ? props.customButtonMargin
                                      : 90
                              }`
                    )}
                    hidden={
                        props.isOpen || props.isAgeLimitExceededForFullInsurance
                    }>
                    <PrimaryButton
                        borderColor={'red'}
                        flexible={true}
                        name={props.insuranceData.name}
                        id={`selector-radio-${props.insuranceData.name}`}
                        onClick={e => {
                            props.handleClick(e);
                            setOpenCoverageIndex(-1);
                        }}
                        className={classNames(
                            `position-bottom z-index-${
                                props.isOpen ? 'below' : 'above'
                            }`,
                            isVillaagarna()
                                ? 'villaagarna-green-bg-color no-border villaagarna-button'
                                : ''
                        )}>
                        <div className={'display-inline-flex align-items'}>
                            <Title
                                tag={'h4'}
                                children={getText(
                                    'webshopsweden.common.cardButton'
                                )}
                            />
                            {!isOkq8() && (
                                <KeyboardArrowDown
                                    color={isVillaagarna() ? '#ffffff' : ''}
                                    size={38}
                                />
                            )}
                        </div>
                    </PrimaryButton>
                </ButtonContainer>
            )}
        </div>
    );

    return isMobile || isTablet || isIPad13 ? renderMobileView() : renderView();
};

export default withWidth<CardsProps>(ExpandedCard);
